/**
 * services/wsService.js
 *
 * Singleton WebSocket client.
 *
 * Lifecycle:
 *   - connect()    — opens (or no-ops if already open/connecting). Idempotent.
 *   - disconnect() — closes and clears reconnect state. Idempotent.
 *
 * Subscriptions:
 *   - subscribe(type, handler)   — register a handler for a typed signal.
 *   - unsubscribe(type, handler) — remove a previously registered handler.
 *
 * Reconnection: exponential backoff with ±20% jitter, capped at 30s.
 * Reconnects only while shouldBeConnected is true (set by connect(),
 * cleared by disconnect()) — logout will not silently reopen the pipe.
 *
 * Privacy: handlers receive `{ type }` only. Message payloads beyond
 * `type` are never read, surfaced, or logged. The WebSocket pipe is a
 * signal channel; all data fetches go through HTTP.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const WS_URL = `${API_URL.replace(/^http/, 'ws')}/ws`;

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

class WsService {
  constructor() {
    this.ws = null;
    this.shouldBeConnected = false;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.handlers = new Map();           // type (string) -> Set<handler>
  }

  connect() {
    if (this.shouldBeConnected && this.ws &&
        (this.ws.readyState === WebSocket.OPEN ||
         this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.shouldBeConnected = true;
    this._open();
  }

  disconnect() {
    this.shouldBeConnected = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, 'client disconnect'); } catch { /* noop */ }
      this.ws = null;
    }
    this.reconnectAttempt = 0;
  }

  subscribe(type, handler) {
    if (typeof type !== 'string' || typeof handler !== 'function') return;
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(handler);
  }

  unsubscribe(type, handler) {
    const set = this.handlers.get(type);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) this.handlers.delete(type);
  }

  _open() {
    if (!this.shouldBeConnected) return;

    let ws;
    try {
      ws = new WebSocket(WS_URL);
    } catch (err) {
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      console.log('[ws] connected');
    };

    ws.onmessage = (event) => {
      let parsed;
      try { parsed = JSON.parse(event.data); }
      catch { return; }
      const type = parsed && typeof parsed.type === 'string' ? parsed.type : null;
      if (!type) return;

      // Privacy: handlers receive only `{ type }`. Even if the server
      // sent additional fields (it should not), they are not forwarded.
      const handlers = this.handlers.get(type);
      if (!handlers || handlers.size === 0) return;
      for (const h of handlers) {
        try { h({ type }); }
        catch (err) { console.error('[ws] handler threw for', type, err); }
      }
    };

    ws.onclose = (event) => {
      console.log('[ws] closed', event.code);
      if (this.ws === ws) this.ws = null;
      if (this.shouldBeConnected) this._scheduleReconnect();
    };

    ws.onerror = () => {
      // onerror always precedes onclose; reconnect is scheduled there.
    };
  }

  _scheduleReconnect() {
    if (!this.shouldBeConnected) return;
    if (this.reconnectTimer) return;

    const base = Math.min(
      INITIAL_BACKOFF_MS * Math.pow(2, this.reconnectAttempt),
      MAX_BACKOFF_MS
    );
    const jitter = base * 0.2 * (Math.random() * 2 - 1);
    const delay = Math.max(0, Math.round(base + jitter));

    this.reconnectAttempt += 1;
    console.log('[ws] reconnecting in', delay, 'ms (attempt', this.reconnectAttempt, ')');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._open();
    }, delay);
  }
}

const wsService = new WsService();
export default wsService;
